import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Briefcase, Check, X, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, money, formatApiError } from "../lib/api";

const run = async (fn, ok, after) => {
  try { await fn(); toast.success(ok); after(); } catch (e) { toast.error(formatApiError(e)); }
};
const FEES = [["broker_booking_fee_pct", "Booking fee"], ["broker_commission_pct", "Commission"], ["broker_owner_pct", "Owner share"]];
const EDIT = ["title", "location", "description", "image_url", "nights", "trip_type", "available_from", "available_to"];

const PartnerRow = ({ b, limit, reload }) => {
  const [note, setNote] = useState("");
  const [fees, setFees] = useState(b.fees);
  const live = JSON.stringify(b.fees);
  useEffect(() => { setFees(JSON.parse(live)); setNote(""); }, [live]);
  const rec = (decision) => run(() => api.post(`/coo/partners/${b.id}/recommend`, { decision, note }), "Sent to the owner for final approval", reload);
  const propose = () => run(() => api.post("/coo/proposals", { kind: "partner_fees", target_id: b.id, fees: Object.fromEntries(Object.entries(fees).map(([k, v]) => [k, Number(v)])), note }), "Fee change sent for approval", reload);
  return (
    <div data-testid={`coo-partner-${b.id}`} className="lux-card p-5 space-y-3">
      <div className="flex flex-wrap justify-between gap-2">
        <p className="font-semibold">{b.company} <span className="text-xs text-slate-500">· {b.contact_name} · licence {b.license_number || "—"}</span></p>
        <span data-testid={`coo-partner-status-${b.id}`} className={`sticker !text-[10px] ${b.status === "approved" ? "!bg-emerald-400 !text-[#0B0F19]" : ""}`}>{b.status}</span>
      </div>
      <p className="text-xs text-slate-500">Insurance: {b.insurance || "—"} · {b.website || "no website"}</p>
      <input data-testid={`coo-partner-note-${b.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the owner" className="lux-input" />
      <div className="flex flex-wrap gap-2">
        <button data-testid={`coo-partner-rec-approve-${b.id}`} onClick={() => rec("approve")} className="btn-gold px-4 py-2 text-xs">Recommend approval</button>
        <button data-testid={`coo-partner-rec-reject-${b.id}`} onClick={() => rec("reject")} className="btn-ghost px-4 py-2 text-xs">Recommend rejection</button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {FEES.map(([k, l]) => (
          <label key={k} className="text-xs text-slate-400">{l} %
            <input data-testid={`coo-fee-${k}-${b.id}`} type="number" step="0.1" value={fees[k]} onChange={(e) => setFees({ ...fees, [k]: e.target.value })} className="lux-input mt-1" />
          </label>
        ))}
      </div>
      <button data-testid={`coo-fee-propose-${b.id}`} onClick={propose} className="btn-ghost px-4 py-2 text-xs">Propose fee change (±{limit}% max)</button>
    </div>
  );
};

const EditDialog = ({ pkg, onClose, reload }) => {
  const [f, setF] = useState({});
  const [types, setTypes] = useState([]);
  useEffect(() => { api.get("/public/trip-types").then((r) => setTypes(Object.entries(r.data.types || r.data).map(([key, v]) => ({ key, label: v.label || key })))); }, []);
  useEffect(() => { if (pkg) setF(Object.fromEntries([...EDIT, "includes", "country", "states"].map((k) => [k, pkg[k] ?? ""]))); }, [pkg]);
  const save = (e) => {
    e.preventDefault();
    run(() => api.put(`/coo/packages/${pkg.id}`, { ...f, country: f.country || "United States", states: Array.isArray(f.states) ? f.states : [], nights: Number(f.nights) || 5, includes: Array.isArray(f.includes) ? f.includes : String(f.includes).split(",") }), "Package updated", () => { onClose(); reload(); });
  };
  return (
    <Dialog open={!!pkg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B0F19] border-white/10 max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit package details</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3" data-testid="coo-edit-form">
          {EDIT.map((k) => (
            <label key={k} className="block text-xs text-slate-400 capitalize">{k.replace(/_/g, " ")}
              {k === "trip_type" ? (
                <select data-testid="coo-edit-trip_type" value={f.trip_type || ""} onChange={(e) => setF({ ...f, trip_type: e.target.value })} className="lux-input mt-1">
                  {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              ) : k === "description" ? (
                <textarea data-testid="coo-edit-description" rows={3} value={f.description ?? ""} onChange={(e) => setF({ ...f, description: e.target.value })} className="lux-input mt-1" />
              ) : (
                <input data-testid={`coo-edit-${k}`} type={k.startsWith("available_") ? "date" : "text"} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="lux-input mt-1" />
              )}
            </label>
          ))}
          <p className="text-xs text-slate-500">Price changes go through "Propose price" and need the owner's approval.</p>
          <button data-testid="coo-edit-save" className="btn-gold w-full py-2.5">Save details</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PendingPackage = ({ p, onEdit, reload }) => {
  const [reason, setReason] = useState("");
  return (
    <div data-testid={`coo-pkg-${p.id}`} className="lux-card p-5 space-y-2">
      <p className="font-semibold">{p.title} <span className="text-xs text-slate-500">· {p.broker_company} · {p.location}</span></p>
      <p className="text-xs text-slate-400">{p.tier} · {p.trip_type} · {p.nights} nights · partner price {money(p.broker_price)}</p>
      <div className="flex flex-wrap gap-2">
        <button data-testid={`coo-pkg-approve-${p.id}`} onClick={() => run(() => api.post(`/admin/packages/${p.id}/approve`), "Package approved and live", reload)} className="btn-gold px-4 py-2 text-xs inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" />Approve</button>
        <button data-testid={`coo-pkg-edit-${p.id}`} onClick={() => onEdit(p)} className="btn-ghost px-4 py-2 text-xs inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" />Edit details</button>
        <input data-testid={`coo-pkg-reason-${p.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What needs changing?" className="lux-input flex-1 min-w-[160px]" />
        <button data-testid={`coo-pkg-reject-${p.id}`} disabled={!reason.trim()} onClick={() => run(() => api.post(`/admin/packages/${p.id}/reject`, { reason }), "Sent back to the partner", reload)} className="btn-ghost px-4 py-2 text-xs inline-flex items-center gap-1 disabled:opacity-40"><X className="w-3.5 h-3.5" />Send back</button>
      </div>
    </div>
  );
};

const PriceRow = ({ p, limit, reload }) => {
  const [price, setPrice] = useState(p.broker_price || "");
  const [note, setNote] = useState("");
  return (
    <div data-testid={`coo-price-${p.id}`} className="flex flex-col md:flex-row md:items-center gap-2 p-4">
      <p className="text-sm flex-1">{p.title} <span className="text-xs text-slate-500">· {p.broker_company} · now {money(p.broker_price)}</span></p>
      <input data-testid={`coo-price-input-${p.id}`} type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="lux-input md:w-32" />
      <input data-testid={`coo-price-note-${p.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason" className="lux-input md:w-48" />
      <button data-testid={`coo-price-propose-${p.id}`} onClick={() => run(() => api.post("/coo/proposals", { kind: "package_price", target_id: p.id, broker_price: Number(price), note }), "Price change sent for approval", reload)} className="btn-ghost px-4 py-2 text-xs">Propose (±{limit}%)</button>
    </div>
  );
};

export default function CooPortal() {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/coo/overview").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="pt-36 text-center text-slate-500">Loading…</p>;
  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 pt-32 pb-24 space-y-10" data-testid="coo-portal">
      <div>
        <p className="eyebrow flex items-center gap-2"><Briefcase className="w-4 h-4" />{d.title}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">Operations</h1>
        <p className="text-sm text-slate-400 mt-3 max-w-2xl">Approve partner packages directly. Partner decisions and pay-rate changes (up to ±{d.limit_pct}%) go to the owner for final approval.</p>
      </div>
      <section className="space-y-3"><p className="eyebrow">Packages waiting for review ({d.pending_packages.length})</p>
        {d.pending_packages.map((p) => <PendingPackage key={p.id} p={p} onEdit={setEdit} reload={load} />)}
        {!d.pending_packages.length && <p data-testid="coo-no-pending" className="text-sm text-slate-500">Nothing waiting.</p>}
      </section>
      <section className="space-y-3"><p className="eyebrow">Partners ({d.brokers.length})</p>
        {d.brokers.map((b) => <PartnerRow key={b.id} b={b} limit={d.limit_pct} reload={load} />)}
        {!d.brokers.length && <p data-testid="coo-no-partners" className="text-sm text-slate-500">No partners yet.</p>}
      </section>
      <section className="space-y-3"><p className="eyebrow">Live package prices</p>
        <div className="lux-card divide-y divide-white/5">{d.live_packages.map((p) => <PriceRow key={p.id} p={p} limit={d.limit_pct} reload={load} />)}</div>
        {!d.live_packages.length && <p className="text-sm text-slate-500">No live partner packages.</p>}
      </section>
      <section className="space-y-2" data-testid="coo-requests"><p className="eyebrow">My requests</p>
        {d.requests.map((a) => (
          <div key={a.id} data-testid={`coo-request-${a.id}`} className="lux-card p-4 text-sm flex flex-wrap justify-between gap-2">
            <span>{a.summary}</span><span className="text-xs text-slate-400">{a.status}{a.decision_note ? ` — ${a.decision_note}` : ""}</span>
          </div>
        ))}
        {!d.requests.length && <p className="text-sm text-slate-500">No requests yet.</p>}
      </section>
      <EditDialog pkg={edit} onClose={() => setEdit(null)} reload={load} />
    </div>
  );
}
